import { useState } from 'react';
import {
  AdminInvoiceDocument,
  INVOICE_DOC_TYPE_LABELS,
  InvoiceDocType,
  invoiceSupplierLabel,
  isCreditNoteDocType,
} from '../../services/invoicesApi';

// Garage documents keep their Spec 051 colours; Garagat's own pair gets its own so the two
// suppliers are distinguishable at a glance and not only by reading the label.
const DOC_TYPE_BADGE: Record<InvoiceDocType, string> = {
  INVOICE: 'order-info',
  CREDIT_NOTE: 'order-teal',
  PLATFORM_INVOICE: 'order-indigo',
  PLATFORM_CREDIT_NOTE: 'order-violet',
};

function docTypeLabel(docType: string): string {
  return INVOICE_DOC_TYPE_LABELS[docType as InvoiceDocType] ?? docType.replace(/_/g, ' ').toLowerCase();
}

const HOLD_REASON_LABELS: Record<string, string> = {
  MISSING_TRN: 'missing TRN',
  MISSING_ADDRESS: 'missing address',
  PLATFORM_FEE_UNSUPPORTED: 'platform fee unsupported',
};

function statusChip(doc: AdminInvoiceDocument) {
  if (doc.status === 'ISSUED') return <span className="admin-badge approved">Issued</span>;
  if (doc.status === 'PENDING_DOCUMENT') return <span className="admin-badge pending">Generating</span>;
  // #466 — ABANDONED is terminal, not a hold. Before it was named here it fell into the `Held:`
  // default below and read as "Held: supplier data", which is the opposite of the truth: nothing
  // is waiting on supplier data, the document can never be generated at all.
  if (doc.status === 'ABANDONED') return <span className="admin-badge rejected">Abandoned</span>;
  const reason = doc.holdReason ? HOLD_REASON_LABELS[doc.holdReason] ?? doc.holdReason : 'supplier data';
  return <span className="admin-badge order-warning">Held: {reason}</span>;
}

/** Delivery states whose `lastError` is worth putting in front of the admin. */
const FAILURE_STATUSES: AdminInvoiceDocument['deliveryStatus'][] = [
  'FAILED', 'BOUNCED', 'SUPPRESSED', 'COMPLAINED', 'DELAYED',
];

function deliveryChip(doc: AdminInvoiceDocument) {
  const to = doc.sentTo ? ` to ${doc.sentTo}` : '';
  switch (doc.deliveryStatus) {
    // #823: SENT is no longer the happy ending — it only means Resend accepted the message.
    // DELIVERED is. Keeping them distinct is the whole point: a bounced invoice used to sit
    // here reading "Sent" for ever.
    case 'DELIVERED':
      return <span className="admin-badge approved">Delivered{to}</span>;
    case 'SENT':
      return <span className="admin-badge order-neutral">Sent{to}</span>;
    case 'DELAYED':
      return <span className="admin-badge order-warning">Delayed — still retrying</span>;
    case 'BOUNCED':
      return doc.deliveryPermanent
        ? <span className="admin-badge rejected">Address rejected</span>
        : <span className="admin-badge order-warning">Bounced — may retry</span>;
    case 'SUPPRESSED':
      return <span className="admin-badge rejected">Blocked by provider</span>;
    case 'COMPLAINED':
      return <span className="admin-badge rejected">Marked as spam</span>;
    case 'FAILED':
      return <span className="admin-badge rejected">Failed</span>;
    case 'NO_EMAIL_ON_FILE':
      return <span className="admin-badge order-warning">No email on file</span>;
    default:
      return <span className="admin-badge order-neutral">Pending</span>;
  }
}

/**
 * What the admin may do about this document's delivery (#823).
 *
 * - A complaint means the recipient reported us as spam. Sending again is the one thing that
 *   must not happen, so there is no button at all.
 * - A permanent bounce or a provider suppression means the SAME address will fail again —
 *   Resend will not even attempt it. Offer only a different address.
 * - Everything else, including a transient bounce, can legitimately be retried as-is.
 */
function resendAction(doc: AdminInvoiceDocument): { label: string; forceOverride: boolean } | null {
  switch (doc.deliveryStatus) {
    case 'COMPLAINED':
      return null;
    case 'SUPPRESSED':
      return { label: 'Send to another address', forceOverride: true };
    case 'BOUNCED':
      return doc.deliveryPermanent
        ? { label: 'Send to another address', forceOverride: true }
        : { label: 'Resend', forceOverride: false };
    case 'DELIVERED':
      return { label: 'Resend', forceOverride: false };
    default:
      return { label: 'Resend', forceOverride: false };
  }
}

interface InvoiceDocumentRowProps {
  doc: AdminInvoiceDocument;
  onDownload: (doc: AdminInvoiceDocument) => void;
  onResend: (doc: AdminInvoiceDocument, forceOverride: boolean) => void;
}

export default function InvoiceDocumentRow({ doc, onDownload, onResend }: InvoiceDocumentRowProps) {
  const [showLog, setShowLog] = useState(false);
  const resend = resendAction(doc);

  return (
    <div className="invoice-doc-row">
      <div className="invoice-doc-main">
        <span className={`admin-badge ${DOC_TYPE_BADGE[doc.docType as InvoiceDocType] ?? 'order-neutral'}`}>
          {docTypeLabel(doc.docType)}
        </span>
        <span className="invoice-doc-number">{doc.invoiceNumber ?? '—'}</span>
        <span className="invoice-doc-date">
          {doc.issueDate ? new Date(doc.issueDate).toLocaleDateString() : '—'}
        </span>
        {statusChip(doc)}
        {deliveryChip(doc)}
        <div className="invoice-doc-actions">
          <button className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => onDownload(doc)}>
            Download
          </button>
          {resend && (
            <button
              className="admin-btn admin-btn-outline admin-btn-sm"
              onClick={() => onResend(doc, resend.forceOverride)}
            >
              {resend.label}
            </button>
          )}
        </div>
      </div>

      <div className="invoice-doc-meta">
        {invoiceSupplierLabel(doc)}
      </div>
      {isCreditNoteDocType(doc.docType) && doc.originalInvoiceNumber && (
        <div className="invoice-doc-meta">Credits invoice {doc.originalInvoiceNumber}</div>
      )}
      {/* The provider's own words are what lets an admin tell a typo from an outage (#823). */}
      {(FAILURE_STATUSES.includes(doc.deliveryStatus) || doc.status === 'ABANDONED') && doc.lastError && (
        <div className="invoice-doc-meta invoice-doc-error">Last error: {doc.lastError}</div>
      )}
      {doc.deliveryStatus === 'COMPLAINED' && (
        <div className="invoice-doc-meta invoice-doc-error">
          The recipient reported this as spam. Do not send to them again.
        </div>
      )}
      {/*
        #466 — the INVOICE_STALE marker now fires once per staleness episode instead of every five
        minutes, so a stuck document is no longer announced repeatedly in the logs. This line is
        how an admin still sees that the sweeper reported it, and when.
      */}
      {doc.staleAlertedAt && doc.status !== 'ABANDONED' && (
        <div className="invoice-doc-meta invoice-doc-error" data-testid="invoice-stale-flag">
          Flagged stale by the sweeper on {new Date(doc.staleAlertedAt).toLocaleString()} — unissued
          or undelivered for over 24h
        </div>
      )}

      <button type="button" className="invoice-link-btn" onClick={() => setShowLog((v) => !v)}>
        {showLog ? 'Hide delivery history' : `Delivery history (${doc.deliveryLog?.length ?? 0})`}
      </button>

      {showLog && (
        (doc.deliveryLog?.length ?? 0) === 0 ? (
          <div className="invoice-doc-meta">No delivery attempts yet</div>
        ) : (
          <table className="invoice-log-table">
            <thead>
              <tr><th>When</th><th>Actor</th><th>Recipient</th><th>Result</th></tr>
            </thead>
            <tbody>
              {doc.deliveryLog.map((entry, i) => (
                <tr key={i}>
                  <td>{new Date(entry.at).toLocaleString()}</td>
                  <td>{entry.actor}</td>
                  <td>{entry.recipient ?? '—'}</td>
                  <td>
                    {entry.result}
                    {entry.error ? <span className="invoice-doc-error"> — {entry.error}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}
